// Topic bridge between the Academy and the written works.
//
// Academy lessons carry author-written `tags`; books carry author-written
// `tags` in src/lib/content.ts. This server function resolves published
// lessons that share a normalized topic with a given tag set, so a book page
// can point at the lessons that teach its subject.

import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { sharedTopics, topicSet } from "@/lib/topics";

function publicClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export type LessonTopicMatch = {
  slug: string;
  title: string;
  summary: string | null;
  domainSlug: string;
  domainName: string;
  topics: string[];
};

export const getLessonsByTopics = createServerFn({ method: "GET" })
  .inputValidator((d: { tags: string[]; limit?: number }) => d)
  .handler(async ({ data }): Promise<LessonTopicMatch[]> => {
    const topics = topicSet(data.tags);
    if (!topics.length) return [];

    // The column stores raw author tags ("human-design"), while topics are
    // normalized ("human design"), so query both spellings.
    const candidates = Array.from(
      new Set(topics.flatMap((t) => [t, t.replace(/ /g, "-")])),
    );

    const supa = publicClient();
    const { data: rows, error } = await supa
      .from("academy_lessons")
      .select("slug, title, summary, tags, academy_domains!inner(slug, name, is_published)")
      .eq("is_published", true)
      .eq("academy_domains.is_published", true)
      .overlaps("tags", candidates)
      .limit(60);
    if (error) throw new Error(error.message);

    return (rows ?? [])
      .map((r) => {
        const domain = r.academy_domains as unknown as { slug: string; name: string };
        return {
          slug: r.slug,
          title: r.title,
          summary: r.summary ?? null,
          domainSlug: domain.slug,
          domainName: domain.name,
          topics: sharedTopics(data.tags, r.tags),
        };
      })
      .filter((m) => m.topics.length > 0)
      .sort((a, b) => b.topics.length - a.topics.length)
      .slice(0, data.limit ?? 4);
  });
